/**
 * Created by Carsten Hibbeler on 21.01.2016.
 * Copyright: xdot GmbH 2015
 */
import { AppContext } from '../app-context';
import FluxReduceStore from 'flux/lib/FluxReduceStore';
import ActionTypesCommon from '../constants/ActionTypesCommon';
import AsyncState from '../constants/AsyncState';
import shallowEqual, { shallowContains } from '../utils/shallowEqual';
import warning from '../utils/warning';
import { generateId } from '../utils/uid-util';

/* there are dependecy problems with StoreFactory
 import { getAsyncOperationStore } from './StoreFactory';
 const AsyncOperationStore = getAsyncOperationStore(AppContext);*/

const deepFreeze = o => {
    Object.freeze(o);
    Object.getOwnPropertyNames(o).forEach(function (prop){
        if (o.hasOwnProperty(prop)
            && o[prop] !== null
            && (typeof o[prop] === 'object' || typeof o[prop] === 'function')
            && !Object.isFrozen(o[prop])) {
            deepFreeze(o[prop]);
        }
    });
    return o;
};

//######################### DEBUG #########################
window.REMOTE_STORES = [];
window.DEBUG_REMOTE_STORES = (contains) => {
    window.REMOTE_STORES.filter(s => !contains || s.storeId.indexOf(contains) >= 0).forEach((s) => {
        const remote = s.getState().remote, lists = {}, items = {}, names = Object.keys(remote);
        names.forEach(name => {
            const d = remote[name], i = { ...d };
            delete i['name'];
            (isList(d) ? lists : items)[name] = i;
        });
        console.group('########### Remote Store ' + s.constructor.name +
            ' (' + (s.className || 'no className') + ') Num data: ' + names.length);
        console.info(s);
        if (Object.keys(lists).length > 0) {
            console.table ? console.table(lists) : console.debug(lists);
        }

        if (Object.keys(items).length > 0) {
            console.table ? console.table(items) : console.debug(items);
        }
        console.groupEnd();
    });
};

const DEFAULT_DATA = {
    __STORE: null,
    name: null,
    className: null,
    api: null,
    query: {},
    //apiMethod: null, deprecated
    _loading: false,
    _asyncKey: false, //string if set
    _invalid: true,
    _error: null
};

export const DEFAULT_DATA_ITEM = {
    ...DEFAULT_DATA,
    item: null, //todo this should be init with null but it will break most item checks :(
    _deleted: false
};

export const DEFAULT_DATA_LIST = {
    ...DEFAULT_DATA,
    item: [],
    _count: 0,
    _start: 0,
    _end: 0,
    _more: false
};

export function isData(data){
    return !!data.__STORE;
}

export function isList(data){
    return Array.isArray(data.item);
}

export function isAttachedList(data, wsUpdate){
    if (wsUpdate.meta && (wsUpdate.meta.attachedClassName && wsUpdate.meta.attachedClassPK)) {
        return isList(data) && data.query.byClassName === wsUpdate.meta.attachedClassName && data.query.byId === wsUpdate.meta.attachedClassPK;
    } else {
        return isList(data) && data.query.byClassName === wsUpdate.type && data.query.byId == wsUpdate.id;
    }

}

export function isItem(data){
    return !isList(data);
}

export function checkValidData(data){
    if (isList(data)) {
        checkValidList(data);
    } else {
        checkValidItem(data);
    }
    return data;
}

export function checkValidDataStore(data, store){
    warning(store.storeId === data.__STORE, 'Item:%s is from different store:%s !', data.__STORE, store.storeId);
}

function _checkValidData(data){
    warning(data.__STORE, 'Store id not set');
    warning(data._invalid || data.item, 'Item is not set');
    warning(typeof data.name === 'string', 'name %s is invalid', data.name);
    warning(typeof data.api === 'string', 'api %s is invalid', data.api);
    Object.keys(data.query).forEach(p => {
        warning(p.indexOf('_') !== 0, 'flag property in query: %s', p); //this may valid but currently we better warn
    });
    warning(!(data._loading && data._error), 'Data is loading and error at the same time');
    warning(!data._loading || (data._loading && data._asyncKey), 'Data is loading and error at the same time');
}

export function checkValidList(data){
    _checkValidData(data);
    Object.keys(DEFAULT_DATA_LIST).forEach(p => {
        warning(typeof data[p] !== 'undefined', 'ListData missing property:%s', p);
    });
    Object.keys(data).forEach(p => {
        warning(DEFAULT_DATA_LIST[p] !== 'undefined', 'Invalid property:%s', p);
    });
    warning(data._count >= data._end, 'Invalid _end:%s for _count:%s', data._end, data._count);
    warning(data._start >= 0, 'Invalid _start ', data._start);
    warning(data._start <= data._end, 'Invalid _start:%s for _end:%s', data._start, data._end);
    warning(data.item.length === data._end, 'Invalid _end:%s for item.length:%s ', data._end, data.item.length);
    warning(data.item.length <= data._count, 'Invalid item.length:%s for _count:%s', data.item.length, data._count);
}

export function checkValidItem(data){
    _checkValidData(data);
    Object.keys(DEFAULT_DATA_ITEM).forEach(p => {
        warning(typeof data[p] !== 'undefined', 'ItemData missing property: ' + p);
    });
    Object.keys(data).forEach(p => {
        warning(DEFAULT_DATA_ITEM[p] !== 'undefined', 'Invalid property: ' + p);
    });
    //warning(data._invalid || ( data.id === data.item.id ), 'Item id is not in sync data.id:%s data.item.id:%s', data.id, data.item.id);
}

export default class RemoteDataStore extends FluxReduceStore {

    constructor(dispatcher){
        super(dispatcher);
        this.storeId = generateId(this.constructor.name);
        this.containedClassnames = {};
        this.defaultListData = { ...DEFAULT_DATA_LIST, __STORE: this.storeId };
        this.defaultItemData = { ...DEFAULT_DATA_ITEM, __STORE: this.storeId };
        window.REMOTE_STORES.push(this);

    }

    static handleExisting(remote, data){
        try {
            const existing = RemoteDataStore.getData(remote, data.name, data.className);
            if (existing) {
                if (shallowEqual(existing.query, data.query)) {
                    console.warn('Init existing data with same query found. Invalidate existing...', data, existing);
                    let newData = RemoteDataStore.invalidateData(existing);
                    if (data.item && data.item !== newData.item) {
                        newData = {
                            ...newData,
                            item: data.item
                        };
                        if (isList(newData)) {
                            newData._start = data._start;
                            newData._end = data._end;
                            newData._count = data._count;
                        }
                    }
                    if (newData === existing) {
                        data = null;
                    } else {
                        data = newData;
                    }
                } else if (existing._asyncKey) {
                    console.warn('Init existing data with running async.', data, existing);
                }

            }
        } catch (e) {
            console.error(e);
        }
        return data;
    }

    static initDataList(name, data, defaults = DEFAULT_DATA_LIST, store){
        warning(!data.item || isList(data), 'Cannot init item with object!');
        warning(typeof defaults._start !== 'undefined', 'Invalid list defaults');
        return this._initData(name, data, defaults, store);
    }

    static initDataItem(name, data, defaults = DEFAULT_DATA_ITEM, store){
        warning(!data.item || isItem(data), 'Cannot init list with object!');
        warning(typeof defaults._start === 'undefined', 'Invalid item defaults');

        //special case for optimistic inits with existing raw item and without a given query
        if (data.item && (!data.query || Object.keys(data.query).length === 0)) {
            console.info('Optimistic init new data from raw item', data);
            warning(data.item.id, 'Cannot optimistic init item from raw item without id!');
            data = {
                ...data,
                query: { id: data.item.id }
            };
        }

        return this._initData(name, data, defaults, store);
    }

    static _initData(name, data, defaults, store){
        if (isData(data)) {
            //this operation is a data copy from one store to another
            console.info('Init new data from valid existing data', data);
            data = {
                ...data
                //item: data.item, -> no clone because item should imutable!?
                //query: data.query -> no clone because query should imutable!?
            };
        } else {
            //console.debug('Init new data', data);
            data = {
                ...defaults,
                ...data,
                query: {
                    ...defaults.query,
                    ...data.query
                }
            };
        }

        data.name = name;
        data.__STORE = store.storeId;
        return checkValidData(data);
    }

    static updateData(existing, data){
        console.debug('Update data', existing);
        warning(!isData(data), 'update with existing data isn\'t a good idea');
        const d = {
            ...existing,
            className: data.className || existing.className,
            api: data.api || existing.api,
            item: data.item || existing.item, //this is useful for optimistic inits -> data is shown but stated as invalid
            _error: data._error || null,
            _loading: false,
            _invalid: true
        };
        if (data.query) {
            d.query = {
                ...existing.query,
                ...data.query
            };
        }
        return d;
    }

    static getData(remote, name, className){
        const data = remote[name];
        return data && (!className || data.className === className) && data;
    }

    static setData(remote, data){
        warning(data.name, 'Named expected');
        if (__DEBUG__) {
            if (data !== remote[data.name] && shallowEqual(data, remote[data.name])) {
                console.error('Setting equal data for remote', remote, data);
            }
            deepFreeze(data);
        }
        return {
            ...remote,
            [data.name]: data
        };
    }

    static updateFlag(existing, flags){
        console.debug('Update flags of', existing, flags);
        warning(!flags.item, 'should not update item');
        warning(!flags.query, 'should not update query');
        warning(!flags.className, 'should not update className');
        warning(existing._invalid || flags._loading || flags._invalid || flags._deleted, 'flag update but not invalid');
        if (shallowContains(existing, flags)) {
            if (__DEBUG__) {
                if (Object.keys(existing).length !== Object.keys(flags).length) {
                    console.error('Flag length incorrect', Object.keys(existing), Object.keys(flags));
                }
            }
            return existing;
        }
        return {
            ...existing,
            ...flags
        };
    }

    static updateQuery(existing, query){
        if (shallowContains(existing.query, query)) {
            return existing;
        }
        return {
            ...existing,
            query: {
                ...existing.query,
                ...query
            }
        };
    }

    static resoreBackup(existing, error){
        console.warn('Restore data because of error', existing, error);
        return existing._backup || this.updateFlag(existing, {
            _loading: false,
            _error: error || 'no-data-backup-to-restore' //TODO use translation-util
        });
    }

    static moreItems(existing, offset = AppContext.DEFAULT_LIST_SIZE){
        //query next chunk
        const newStart = existing._end > existing._count ? existing._count : existing._end;
        return this.updateQuery(this.invalidateData(existing), {
            start: newStart,
            end: newStart + offset
        });
    }

    static invalidateData(existing){
        if (existing._invalid) {
            return existing;
        }
        if (isList(existing) && (existing._start !== existing.query.start || existing._end !== existing.query.end)) {
            //for paged lists we want to invalidate the full list not only the current page
            console.warn('reset list - correct?');
            existing = this.updateQuery(existing, {
                start: 0,
                end: existing._end || AppContext.DEFAULT_LIST_SIZE
            });
        }
        return {
            ...existing,
            _invalid: true,
            _backup: existing
        };
    }

    static removeRemoteData(remote, name){
        remote = {
            ...remote
        };
        delete remote[name];
        return remote;
    }

    static updateList(existing, dataUpdate){
        let items, query = existing.query;

        if (query.end > dataUpdate._count) {
            query = {
                ...query,
                end: dataUpdate._count
            };
        }

        /* TODO this seems not correct: dataUpdate._start > dataUpdate._count is invalid and should be handled on the server
         if (dataUpdate._start > dataUpdate._count) { //Fix if asked result is above real data
         const gap = dataUpdate._end - dataUpdate._start;
         _end = dataUpdate._count;
         _start = dataUpdate._end - gap > 0 ? dataUpdate._end - gap : 0;
         }*/

        if (existing && existing.item) {
            items = existing.item.slice();
            //todo add validity checks (no gaps etc)
            //ToDo: Wrong Display in Lists, cause length is e.g. 5 and no item is in array, if no data was fetched.
            if (items.length < Math.max(existing._end, dataUpdate._end)) {
                items.length = Math.max(existing._end, dataUpdate._end);
            }

            if (items.length > dataUpdate._count) {
                items.length = dataUpdate._count;
            }

            let i = dataUpdate._start;
            dataUpdate.item.forEach(e => {
                items[i++] = e;
            });
        } else {
            items = dataUpdate.item;
        }

        return {
            name: existing.name,
            className: existing.className,
            api: dataUpdate.api || existing.api,
            query: query,
            item: items,
            _count: dataUpdate._count,
            _start: existing._start,
            _end: dataUpdate._end,
            _more: dataUpdate._more,
            _backup: null,
            _loading: false,
            _invalid: false,
            _asyncKey: false,
            _error: null,
            __STORE: existing.__STORE
        };
    }

    static updateItem(existing, dataUpdate){
        return {
            ...existing,
            item: dataUpdate,
            _backup: null,
            _loading: false,
            _invalid: false,
            _asyncKey: false,
            _error: null
        };
    }

    static remoteStatetoDataArray(remote){
        return Object.keys(remote).map(name => remote[name]);
    }

    containsRemoteData(name, className = null){
        return !!RemoteDataStore.getData(this.getRemoteState(), name, className);
    }

    getInitialState(){
        return { remote: {} };
    }

    refreshContainedClassNames(remote){
        const classNameMap = {};
        RemoteDataStore.remoteStatetoDataArray(remote).forEach(d => {
            [].concat(this.getDataClassNames(d)).forEach(nc => classNameMap[nc] = true);
        });
        this.containedClassnames = classNameMap;
    }

    getDataClassNames(d){
        return d.className;
    }

    getContainedClassnames(){
        return this.containedClassnames;
    }

    getRemoteState(){
        return this.getState().remote;
    }

    getRequiredRemoteDataUpdates(){
        return RemoteDataStore.remoteStatetoDataArray(this.getRemoteState()).filter(d => d._invalid && !d._loading && !d._error);
    }

    isActive(){
        //ToDo: Listeners will be wired by FluxContaienr after super.componentDidMount was called
        //https://github.com/facebook/flux/blob/master/src/FluxContainer.js
        //return this.__emitter.listeners('change').length > 0;
        return true;
    }

    getRemoteData(name){
        warning(name, 'getRemoteData without name');
        return this.getRemoteState()[name];
    }

    getDataBackup(name){
        return this.getRemoteState()[name]._backup;
    }

    initList(state, name, data, defaultListData){
        data = RemoteDataStore.initDataList(name, data, defaultListData, this);
        if (__DEBUG__) {
            console.debug('RemoteDataStore initList ', {
                'api': data.api,
                name,
                data,
                defaultListData,
                currentListState: state.remote[name]
            });
        }
        data = RemoteDataStore.handleExisting(state.remote, data);
        if (data) {
            const remote = RemoteDataStore.setData(state.remote, data);
            return this.doSetRemoteState(state, remote);
        } else {
            return state;
        }
    }

    initItem(state, name, data, defaultItemData){
        data = RemoteDataStore.initDataItem(name, data, defaultItemData, this);
        if (__DEBUG__) {
            console.debug('RemoteDataStore initItem ', {
                name,
                data,
                defaultItemData,
                currentListState: state.remote[name]
            });
        }
        data = RemoteDataStore.handleExisting(state.remote, data);
        if (data) {
            const remote = RemoteDataStore.setData(state.remote, data);
            return this.doSetRemoteState(state, remote);
        } else {
            return state;
        }
    }

    removeRemoteData(state, name){
        const remote = RemoteDataStore.removeRemoteData(state.remote, name);
        return this.doSetRemoteState(state, remote);
    }

    validateRemoteState(remote){
        RemoteDataStore.remoteStatetoDataArray(remote).forEach(data => {
            this.validateRemoteData(data);
            warning(data === remote[data.name], 'invalid data name %s!=%s', data.name, remote[data.name].name);
        });
    }

    validateRemoteData(data){
        checkValidData(data);
        checkValidDataStore(data, this);
    }

    doSetRemoteState(state, remote){
        this.validateRemoteState(remote);
        this.refreshContainedClassNames(remote);
        return {
            ...state,
            remote
        };
    }

    reduce(state, action){
        let remote = state.remote, data, newData;

        switch (action.type) {

            case ActionTypesCommon.DATA_ASYNC:
                data = RemoteDataStore.getData(remote, action.meta.name, action.meta.className);

                if (data) {
                    // We have dependency problems when we do this!
                    // this.getDispatcher().waitFor([ AsyncOperationStore.getDispatchToken() ]);
                    const asyncKey = action.meta.key;

                    switch (action.meta.asyncState) {
                        case AsyncState.REQUESTED:
                            newData = RemoteDataStore.updateFlag(data, { _loading: true, _asyncKey: asyncKey });
                            break;

                        case AsyncState.SUCCESS:
                            if (data._asyncKey === asyncKey) {
                                newData = this.asyncSuccess(data, action.payload);
                                warning(!newData._loading, 'Loading after success');
                            } else {
                                console.error('Ignore invalid async ', data, action);
                            }
                            break;

                        case AsyncState.ERROR:
                            if (data._asyncKey === asyncKey) {
                                newData = RemoteDataStore.resoreBackup(data, action.payload);
                                warning(!newData._loading, 'Loading after error');
                            } else {
                                console.error('Ignore invalid async ', data, action);
                            }

                            break;
                        //no default
                    }
                }

                break;

            case ActionTypesCommon.MORE_ITEMS:
                data = RemoteDataStore.getData(remote, action.meta.name, action.meta.className);
                if (data) {
                    let offset = action && action.payload && action.payload.query && action.payload.query.offset || undefined;
                    newData = RemoteDataStore.moreItems(data, offset);
                }
                break;

            case ActionTypesCommon.DATA_UPDATE_MESSAGE:
                remote = this.websocketModelUpdate(remote, action.payload);
                break;

            case ActionTypesCommon.DATA_INVALIDATE:
                data = RemoteDataStore.getData(remote, action.meta.name, action.meta.className);
                if (data) {
                    newData = RemoteDataStore.invalidateData(data);
                }
                break;

            //no default
        }

        if (newData) {
            remote = RemoteDataStore.setData(remote, newData);
        }

        if (state.remote !== remote) {
            if (__DEBUG__) {
                console.debug(this.constructor.name, {
                    type: action.type,
                    action,
                    remote: state.remote,
                    newRemote: remote
                });
            }
            state = this.doSetRemoteState(state, remote);

        }

        return state;
    }

    asyncSuccess(data, dataUpdate){
        if (isItem(data)) {
            return RemoteDataStore.updateItem(data, dataUpdate);
        } else {
            return RemoteDataStore.updateList(data, dataUpdate);
        }
    }

    websocketModelUpdate(remote, wsUpdate){
        let newRemote;
        let wsUpdates = [];
        if (!Array.isArray(wsUpdate)) {
            wsUpdates.push(wsUpdate);
        } else {
            wsUpdate.forEach(wsEvent => {
                    if (wsEvent instanceof String) {
                        wsUpdates.push(JSON.parse(wsEvent));
                    } else {
                        wsUpdates.push(wsEvent);
                    }
                }
            );
        }

        let processedListNames = [];

        wsUpdates.forEach(wsUpdate => {

            if (wsUpdate.eventType === 'POST' || wsUpdate.eventType === 'PUT' || wsUpdate.eventType === 'PATCH' || wsUpdate.eventType === 'PATCH-MERGE' || wsUpdate.eventType === 'DELETE' || wsUpdate.eventType === 'DELETE') {
                RemoteDataStore.remoteStatetoDataArray(remote).filter(d => d.className === wsUpdate.type || (wsUpdate.metadata && d.className === wsUpdate.metadata.attachedClassName)).forEach(d => {
                    const o = d;
                    const wasProcessed = processedListNames.indexOf(o.name) >= 0;
                    if (isList(o) && !wasProcessed && this.shouldUpdateList(o, wsUpdate)) {
                        if (__DEBUG__) {
                            console.debug('Update', wsUpdate, '==> invalidate list', o);
                        }
                        d = RemoteDataStore.invalidateData(o);
                    } else if (o && o.item && o.item.id == wsUpdate.id) {
                        console.debug('Update', wsUpdate, '==> invalidate corresponding item', o);
                        if (wsUpdate.eventType === 'POST') {
                            console.error('Model-Update->', wsUpdate, ' for existing object', wsUpdate, o);
                        } else if (wsUpdate.eventType === 'DELETE') {
                            d = RemoteDataStore.updateFlag(o, {
                                _deleted: true,
                                _error: null,
                                _loading: false,
                                _invalid: false
                            });
                        } else {
                            d = RemoteDataStore.invalidateData(o);
                        }
                    } else if (wsUpdate.metadata && o && o.item && o.item.id === wsUpdate.metadata.attachedClassPK) {
                        console.debug('Update', wsUpdate, '==> invalidate item (attached)', o);
                        d = RemoteDataStore.invalidateData(o);
                    }

                    if (o !== d) {
                        newRemote = (newRemote || (newRemote = { ...remote }));
                        newRemote[o.name] = d;
                        processedListNames.push(o.name);
                    }

                });

                RemoteDataStore.remoteStatetoDataArray(remote).filter(d => isAttachedList(d, wsUpdate)).forEach(d => {
                    const o = d;
                    console.debug('Update', wsUpdate, '==> invalidate item (attached List)', o);
                    d = RemoteDataStore.invalidateData(o);
                    if (d !== o) {
                        newRemote = (newRemote || (newRemote = { ...remote }));
                        newRemote[d.name] = d;
                    }
                });
            } else if (!wsUpdate.metric) {
                console.warn('Unknown update type: ', wsUpdate);
            }
        });

        if (this.extendedWebsocketModelUpdate) {
            newRemote = this.extendedWebsocketModelUpdate(newRemote || remote, wsUpdate);
        }

        return newRemote || remote;
    }

    shouldUpdateList(d, wsUpdate){
        // debugger;
        //TODO implement
        if (d.className === wsUpdate.type) {
            return true;
        } else if (wsUpdate.metadata && d.className === wsUpdate.metadata.attachedClassName) {
            return true;
        }
        return false;
    }
}






// WEBPACK FOOTER //
// ./js/stores/RemoteDataStore.js